// Issue list item component
// Reference: spec.md US2 - display issue in list

import { Link } from 'react-router-dom';
import type { IssueWithRelations, IssueStatus, IssueCategory } from '@propman/shared';

interface IssueListItemProps {
  issue: IssueWithRelations;
}

// Status badge colors
const STATUS_COLORS: Record<IssueStatus, { bg: string; text: string }> = {
  reported: { bg: 'bg-amber-100', text: 'text-amber-800' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-800' },
  job_awarded_awaiting_appointment: { bg: 'bg-purple-100', text: 'text-purple-800' },
  completed: { bg: 'bg-green-100', text: 'text-green-800' },
};

// Status labels
const STATUS_LABELS: Record<IssueStatus, string> = {
  reported: 'Reported',
  in_progress: 'In Progress',
  job_awarded_awaiting_appointment: 'Awaiting Appointment',
  completed: 'Completed',
};

// Category labels
const CATEGORY_LABELS: Record<IssueCategory, string> = {
  mechanical: 'Mechanical',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  handyman: 'Handyman',
  cleaning: 'Cleaning',
  security_access: 'Security & Access',
  fire_safety: 'Fire Safety',
  lift_elevator: 'Lift / Elevator',
  pest_control: 'Pest Control',
  landscaping_grounds: 'Landscaping',
  appliances: 'Appliances',
  it_intercom: 'IT & Intercom',
  other: 'Other',
};

/**
 * Format relative time (e.g., "2 days ago")
 */
function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 30) {
    const diffMonths = Math.floor(diffDays / 30);
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }
  if (diffDays > 0) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  if (diffMins > 0) {
    return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  }
  return 'Just now';
}

export function IssueListItem({ issue }: IssueListItemProps) {
  const statusColor = STATUS_COLORS[issue.status];
  const statusLabel = STATUS_LABELS[issue.status];
  const categoryLabel = CATEGORY_LABELS[issue.category];
  const relativeTime = formatRelativeTime(issue.createdAt);

  return (
    <Link
      to={`/issues/${issue.id}`}
      className="block card hover:shadow-md transition-shadow"
    >
      <div className="p-4">
        {/* Header row: Reference + Status */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-mono text-gray-500">
            {issue.referenceNumber}
          </span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}
          >
            {statusLabel}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-base font-medium text-gray-900 truncate">
          {issue.title}
        </h3>

        {/* Location */}
        <p className="mt-1 text-sm text-gray-600 truncate">
          {issue.unit.property.name} - {issue.unit.identifier}
          {issue.unit.floor && ` (${issue.unit.floor})`}
        </p>

        {/* Meta row: Category + Time */}
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <span className="inline-flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {categoryLabel}
          </span>
          <span>Raised {relativeTime}</span>
        </div>

        {/* Attachments indicator */}
        {issue.attachments && issue.attachments.length > 0 && (
          <div className="mt-2 flex items-center text-xs text-gray-500">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            {issue.attachments.length} attachment{issue.attachments.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </Link>
  );
}

export default IssueListItem;
